import axios from 'axios';

import { CLASSROOM_STATE, PARAMS } from '../../const';
import { manageArrayResponse, manageObjectResponse } from './apiResponse';
import {
  API_CLASSROOMS_ASSIGNMENTS,
  API_CLASSROOMS_ASSIGNMENTS_PAGINATION,
  API_CLASSROOMS_ASSIGNMENTS_TASKS,
  API_CLASSROOMS_ASSIGNMENTS_TASKS_USERS,
  API_CLASSROOMS_TASKS_CONTENT,
  API_CLASSROOMS_TASKS_INDIVIDUAL,
  API_CLASSROOMS_TASKS_GROUP,
  API_SECTIONS_ASSIGNMENTS,
  API_USERS_TASKS
} from './apiConstV2';
import { V3_API_CLASSROOMS_ASSIGNMENTS_TASKS } from './apiConstV3';
import { getLogger } from '../logger';
import {
  API_BASIC_ANALYTICS,
  API_CLASSROOM,
  API_CLASSROOM_BOOK_CSV,
  API_CLASSROOM_IMPORT_UNIT,
  API_CLASSROOM_GOOGLE_COURSES,
  API_CLASSROOM_STUDENTS_CSV,
  API_CLASSROOM_VIDEOCALL,
  API_CLASSROOM_VIDEOCALL_UPDATE_OR_CREATE,
  API_CLASSROOMS,
  API_CLASSROOMS_GROUPS,
  API_CLASSROOMS_STUDENTS,
  API_CLASSROOMS_TEACHERS,
  API_CLASSROOMS_PARTICIPANTS,
  API_SECTION_PIN_CODES,
  API_USER_SECTIONS,
  API_USERS_CLASSROOM,
  API_USERS_CLASSROOMS,
  API_CLASSROOM_GOOGLE_LAUNCH,
  API_USERS_ASSIGNMENTS_PAGINATION,
  API_CLASSROOM_IMPORT_LESSON,
  API_CLASSROOM_GOOGLE_SHARE_CONTENT
} from './apiConst';

const debug = getLogger('services:api:classroomApi');

export const getActiveClassrooms = userId => {
  if (userId) {
    return manageArrayResponse(
      axios.get(`${API_USERS_CLASSROOMS}?state=${CLASSROOM_STATE.ACTIVE.state}`.replace(PARAMS.USER, userId))
    );
  }
  debug.error('[getActiveClassrooms] User id is empty');
};

export let getArchiveClassrooms = userId => {
  if (userId) {
    return manageArrayResponse(
      axios.get(`${API_USERS_CLASSROOMS}?state=${CLASSROOM_STATE.ARCHIVED.state}`.replace(PARAMS.USER, userId))
    );
  }
  debug.error('[getArchiveClassrooms] User id is empty');
};

export let changeClassroomsOrder = (userId, idPositions) => {
  if (userId && idPositions) {
    return axios.put(API_USER_SECTIONS.replace(PARAMS.USER, userId), idPositions);
  }
  debug.error('[changeClassroomsOrder] User id or classroom positions are empty');
};

export let enrollClassroom = (userId, body) => {
  if (userId && body) {
    return axios.post(API_USERS_CLASSROOMS.replace(PARAMS.USER, userId), body);
  }
  debug.error('[enrollClassroom] User id or body are empty');
};

export let unenrollStudent = (userId, classroomId) => {
  if (userId && classroomId) {
    return axios.delete(API_USERS_CLASSROOM.replace(PARAMS.USER, userId).replace(PARAMS.CLASSROOM, classroomId));
  }
  debug.error('[unenrollStudent] User id or classroomId are empty');
};

export let addClassroom = classroom => {
  if (classroom) {
    return axios.post(API_CLASSROOMS, classroom);
  }
  debug.error('[addClassroom] Classroom is empty');
};

export let editClassroom = classroom => {
  if (classroom) {
    const url = API_CLASSROOM.replace(PARAMS.CLASSROOM, classroom.id);
    delete classroom.id;
    return axios.put(url, classroom);
  }
  debug.error('[editClassroom] Classroom is empty');
};

export let archiveClassroom = (classroomId, context) => {
  if (classroomId) {
    const payload = { state: CLASSROOM_STATE.ARCHIVED.code };
    if (context) {
      payload.context = context;
    }
    return axios.patch(API_CLASSROOM.replace(PARAMS.CLASSROOM, classroomId), payload);
  }
  debug.error('[archiveClassroom] ClassroomId is empty');
};

export let recoverClassroom = classroomId => {
  if (classroomId) {
    return axios.patch(API_CLASSROOM.replace(PARAMS.CLASSROOM, classroomId), { state: CLASSROOM_STATE.ACTIVE.code });
  }
  debug.error('[recoverClassroom] ClassroomId is empty');
};

export let getClassroom = classroomId => {
  if (classroomId) {
    return manageObjectResponse(axios.get(`${API_CLASSROOMS}/${classroomId}`));
  }
  debug.error('[getClassroom] Classroom id is empty');
};

export const getStudents = classroomId => {
  if (classroomId) {
    let url = API_CLASSROOMS_STUDENTS.replace(PARAMS.CLASSROOM, classroomId);
    return manageArrayResponse(axios.get(url));
  }
  debug.error('[getStudents] Classroom id is empty');
};

export const getClassroomParticipants = classroomId => {
  if (classroomId) {
    const participantsUrl = API_CLASSROOMS_PARTICIPANTS.replace(PARAMS.CLASSROOM, classroomId);
    return manageObjectResponse(axios.get(participantsUrl));
  }
  debug.error('[getClassroomParticipants] Classroom id is empty');
};

export function getClassroomStudentPinCodes(sectionId, force = false) {
  if (sectionId) {
    let url = API_SECTION_PIN_CODES.replace(PARAMS.SECTION, sectionId);
    url = force ? `${url}?force=true` : url;
    return manageArrayResponse(
      axios.get(url, {
        responseType: 'arraybuffer',
        headers: {
          'file-format': 'arraybuffer'
        }
      })
    );
  }
  debug.error('[getClassroomStudentPinCodes] Classroom section id is empty');
}

export const getPaginatedAssignments = (classroomId, userId, page, filterParams, perPage = 4) => {
  const { status, userId: filterUser, startDate, finishDate, keywords } = filterParams;
  let query = '';
  page = page ? page : 1;

  if (filterUser) {
    query += `&userId=${filterUser}`;
  }
  if (startDate && finishDate) {
    startDate.setHours(0);
    startDate.setMinutes(0);
    startDate.setSeconds(0);
    finishDate.setHours(23);
    finishDate.setMinutes(59);
    finishDate.setSeconds(59);

    query += `&startDate=${startDate.getTime()}&finishDate=${finishDate.getTime()}`;
  }

  if (keywords) {
    query += `&keywords=${keywords}`;
  }

  if (status && status.key) {
    query += `&status=${status.key}`;
  }

  if (classroomId) {
    let url = API_CLASSROOMS_ASSIGNMENTS_PAGINATION.replace(PARAMS.CLASSROOM, classroomId)
      .replace(PARAMS.PAGE, page)
      .replace(PARAMS.ITEMS, perPage.toString());

    return manageArrayResponse(axios.get(`${url}${query}&currentDate=${Date.now()}`));
  } else {
    let url = API_USERS_ASSIGNMENTS_PAGINATION.replace(PARAMS.USER, userId)
      .replace(PARAMS.PAGE, page)
      .replace(PARAMS.ITEMS, perPage.toString());

    return manageArrayResponse(axios.get(`${url}${query}&currentDate=${Date.now()}`));
  }
};

export const getAllAssignments = userId => {
  if (userId) {
    let url = API_USERS_TASKS.replace(PARAMS.USER, userId);
    return manageArrayResponse(axios.get(url));
  }
  debug.error('[getAllAssignments] User id is empty');
};

export const getStudentsFromTask = (classroomId, taskId) => {
  if (classroomId && taskId) {
    let url = API_CLASSROOMS_ASSIGNMENTS_TASKS_USERS.replace(PARAMS.CLASSROOM, classroomId).replace(
      PARAMS.TASK,
      taskId
    );
    return manageArrayResponse(axios.get(url));
  }
  debug.error('[getStudentsFromTask] Classroom id or task id are empty');
};

export const updateStudentsFromTask = (classroomId, taskId, users) => {
  if (classroomId && taskId) {
    let url = API_CLASSROOMS_ASSIGNMENTS_TASKS_USERS.replace(PARAMS.CLASSROOM, classroomId).replace(
      PARAMS.TASK,
      taskId
    );
    return manageArrayResponse(axios.put(url, users));
  }
  debug.error('[updateStudentsFromTask] Classroom id or task id are empty');
};

export const getAssignmentById = (classroomId, taskId) => {
  if (classroomId && taskId) {
    let url = V3_API_CLASSROOMS_ASSIGNMENTS_TASKS.replace(PARAMS.CLASSROOM, classroomId).replace(PARAMS.TASK, taskId);

    return manageArrayResponse(axios.get(url));
  }
  debug.error('[getAssignmentById] User id is empty');
};

export let createAssignments = (assignment, classroomId) => {
  if (classroomId) {
    let url = API_CLASSROOMS_ASSIGNMENTS.replace(PARAMS.CLASSROOM, classroomId);
    return manageArrayResponse(axios.post(url, assignment));
  }
  debug.error('[createAssignments] Classroom id is empty');
};

export let createContentTask = (assignment, classroomId) => {
  if (classroomId) {
    const url = API_CLASSROOMS_TASKS_CONTENT.replace(PARAMS.CLASSROOM, classroomId);
    return manageArrayResponse(axios.post(url, assignment));
  }
  debug.error('[createContentTask] Classroom id is empty');
};

export let createIndividualTask = (assignment, classroomId) => {
  if (classroomId) {
    const url = API_CLASSROOMS_TASKS_INDIVIDUAL.replace(PARAMS.CLASSROOM, classroomId);
    return manageArrayResponse(axios.post(url, assignment));
  }
  debug.error('[createIndividualTask] Classroom id is empty');
};

export let createGroupTask = (assignment, classroomId) => {
  if (classroomId) {
    const url = API_CLASSROOMS_TASKS_GROUP.replace(PARAMS.CLASSROOM, classroomId);
    return manageArrayResponse(axios.post(url, assignment));
  }
  debug.error('[createGroupTask] Classroom id is empty');
};

export let editAssignments = (assignment, classroomId, taskId) => {
  if (classroomId && taskId) {
    let url = API_CLASSROOMS_ASSIGNMENTS_TASKS.replace(PARAMS.CLASSROOM, classroomId).replace(PARAMS.TASK, taskId);
    debug('[editAssignments] url "%s" | assignment: %O', url, assignment);
    return manageArrayResponse(axios.put(url, assignment));
  }
  debug.error('[editAssignments] Classroom id and/or task id is empty');
};

export const deleteAssignment = task => {
  if (task.parentSection) {
    let url = API_SECTIONS_ASSIGNMENTS.replace(PARAMS.SECTION, task.parentSection);
    return manageArrayResponse(axios.delete(`${url}/${task.id}`));
  }
  debug.error('[deleteAssignment] section is empty');
};

export const getGroups = classroomId => {
  if (classroomId) {
    let url = API_CLASSROOMS_GROUPS.replace(PARAMS.CLASSROOM, classroomId);
    return manageArrayResponse(axios.get(url));
  }
  debug.error('[getGroups] Classroom id is empty');
};

export let getImportedStudents = (sectionId, formData) => {
  if (sectionId) {
    let url = API_CLASSROOM_STUDENTS_CSV.replace(PARAMS.CLASSROOM, sectionId);
    return manageObjectResponse(axios.post(url, formData));
  }
  debug.error('[getImportedStudents] Section id is empty');
};

export const getBasicAnalytics = (sectionId, date, studentIds = null) => {
  if (sectionId && date) {
    let timeZone = 'UTC';
    try {
      timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (e) {
      debug.error('[getBasicAnalytics] cant resolve user timezone, using UTC');
    }
    let url = API_BASIC_ANALYTICS.replace(PARAMS.SECTION, sectionId)
      .replace(PARAMS.MONTH, date.month)
      .replace(PARAMS.YEAR, date.year)
      .replace(PARAMS.TIME_ZONE, timeZone);

    if (studentIds && studentIds.length > 0) {
      const studentIdsParam = studentIds.map(id => `studentIds=${encodeURIComponent(id)}`).join('&');
      url += `&${studentIdsParam}`;
    }

    return manageObjectResponse(axios.get(url));
  }
  debug.error('[getBasicAnalytics] section id and/or date is empty');
};

export const getBookCsv = (sectionId, bookId) => {
  if (sectionId && bookId) {
    let url = API_CLASSROOM_BOOK_CSV.replace(PARAMS.SECTION, sectionId).replace(PARAMS.BOOK, bookId);

    return manageObjectResponse(axios.get(url));
  }
  debug.error('[getBookCsv] book id and/or sectionId is empty');
};

export const getClassroomTeachers = async classroomId => {
  if (classroomId) {
    let url = API_CLASSROOMS_TEACHERS.replace(PARAMS.CLASSROOM, classroomId);
    let response = await manageObjectResponse(axios.get(url));
    return response.data;
  }
  debug.error('[getClassroomTeachers] Classroom id is empty');
};

export const getTaskLessonsAndActivities = (classroomId, taskId) => {
  if (classroomId && taskId) {
    let url = API_CLASSROOMS_ASSIGNMENTS_TASKS.replace(PARAMS.CLASSROOM, classroomId).replace(PARAMS.TASK, taskId);

    return manageObjectResponse(axios.get(url));
  }
  debug.error('[getTaskLessonsAndActivities] sectionId and/or taskId is empty');
};

export const getVideocallInfo = classroomId => {
  if (classroomId) {
    const url = API_CLASSROOM_VIDEOCALL.replace(PARAMS.CLASSROOM_ID, classroomId);
    const res = manageObjectResponse(axios.get(url));
    return res;
  }
  debug.error('[getVideocallInfo] no classroomId provided');
};

export const updateOrCreateVideocallInfo = (classroomId, videocallUrl) => {
  if (classroomId && videocallUrl) {
    const url = API_CLASSROOM_VIDEOCALL_UPDATE_OR_CREATE;
    const body = { classroomId, videocallUrl };
    const res = manageObjectResponse(axios.put(url, body));
    return res;
  }
  debug.error('[getVideocallInfo] no classroomId provided');
};

export const importUnitsIntoBook = (
  classroomId,
  bookId,
  unitActivityId,
  fromBookActivityId,
  fromSectionId,
  context,
  unlinked
) => {
  const url = API_CLASSROOM_IMPORT_UNIT.replace(PARAMS.CLASSROOM, classroomId).replace(PARAMS.BOOK, bookId);
  const body = { unitActivityIds: [unitActivityId], fromBookActivityId };
  if (fromSectionId) {
    body.fromSectionId = fromSectionId;
  }
  if (unlinked !== undefined) {
    body.unlinked = unlinked;
  }
  if (context) {
    body.context = context;
  }
  return manageObjectResponse(axios.put(url, body));
};

export const importLessonsIntoUnit = (
  classroomId,
  bookId,
  unitActivityId,
  lessonActivityIds,
  context,
  fromSectionId,
  unlinked
) => {
  const url = API_CLASSROOM_IMPORT_LESSON.replace(PARAMS.CLASSROOM, classroomId).replace(PARAMS.BOOK, bookId);
  const body = { unitActivityId, lessonActivityIds };
  if (fromSectionId) {
    body.fromSectionId = fromSectionId;
  }
  if (unlinked !== undefined) {
    body.unlinked = unlinked;
  }
  if (context) {
    body.context = context;
  }
  return manageObjectResponse(axios.put(url, body));
};

export const shareContentInGoogleClassroomFromBackend = (sectionId, institutionId, courseId, data) => {
  const url = API_CLASSROOM_GOOGLE_SHARE_CONTENT.replace(PARAMS.GOOGLE_COURSE_ID, courseId);

  return manageObjectResponse(axios.post(url, { sectionId, institutionId, data }));
};

export const getGoogleClassroomCoursesFromBackend = () => {
  return manageObjectResponse(axios.get(API_CLASSROOM_GOOGLE_COURSES));
};

export const getContentLaunchUrl = (
  classId,
  activityId,
  language,
  lis_outcome_service_url = undefined,
  lis_resource_id = undefined
) => {
  let url = API_CLASSROOM_GOOGLE_LAUNCH.replace(PARAMS.CLASSROOM_ID, classId);

  if (lis_outcome_service_url) {
    url = `${url}?lis_outcome_service_url=${encodeURIComponent(
      lis_outcome_service_url
    )}&lis_result_sourcedid=${encodeURIComponent(lis_resource_id)}`;
  }

  const body = { activityId, language };
  return manageObjectResponse(axios.post(url, body));
};
